import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Dropdown, Spin } from 'antd';
import { CloseCircleFilled, CloseOutlined, SearchOutlined } from '@ant-design/icons';
import { useDataProvider } from 'react-admin';
import { useHistory } from 'react-router-dom';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { faRocket, faThLarge } from '@fortawesome/free-solid-svg-icons';
import { ExtendedDataProvider } from '../../types';
import FaIcon from '../Icons/FaIcon';
import './SearchBar.css';

type SearchModel = 'Service' | 'Application' | 'ServiceItem';

type SearchHit = {
    id: number,
    name: string,
    model: SearchModel,
    pov: string,
};

/* The endpoint searches names, and narrows to one exact service item by
   service_item_id or change_instance_id. Picking one of those is picking a
   different question, so it is not a hidden mode: it sits in the bar as a
   token, in front of what was typed, and the bar reads as the question it is
   about to ask. No token means the plain name search, which is the default. */
type FilterKey = 'service_item_id' | 'change_instance_id';

const FILTERS: { key: FilterKey, label: string, hint: string, placeholder: string }[] = [
    {
        key: 'service_item_id',
        label: 'Service item ID',
        hint: 'go straight to one service item',
        placeholder: 'Enter a service item id',
    },
    {
        key: 'change_instance_id',
        label: 'Change instance ID',
        hint: 'find the service item a change belongs to',
        placeholder: 'Enter a change instance id',
    },
];

/* The models the endpoint looks into, in the order they are listed. The
   endpoint returns them newest first, all three mixed together; grouping wins
   here, because someone hunting for an application does not want it three rows
   below a service item that happens to be younger. */
const GROUPS: {
    model: SearchModel,
    label: string,
    icon: IconDefinition,
    path: (hit: SearchHit) => string,
}[] = [
    {
        model: 'Service',
        label: 'Services',
        icon: faRocket,
        path: hit => `/${hit.pov === 'consumer' ? 'browse_services' : 'services'}/#${hit.name}`,
    },
    {
        model: 'Application',
        label: 'Applications',
        icon: faThLarge,
        path: hit => `/${hit.pov === 'consumer' ? 'applications' : 'owner_applications'}/#${hit.name}`,
    },
    {
        model: 'ServiceItem',
        label: 'Service Items',
        // an item is an instance of a service, and wears the service's mark
        icon: faRocket,
        path: hit => `/${hit.pov === 'consumer' ? 'service_items' : 'owner_service_items'}/${hit.id}`,
    },
];

const POV_LABELS: { [pov: string]: string } = {
    consumer: 'Consumer',
    serviceowner: 'Service Owner',
};

/* What the panel asks for. The endpoint pages at 20 by default, but the panel
   is a glance rather than a report: a short page keeps it inside the viewport,
   and the footer owns up to what was left out. */
const PAGE_SIZE = 10;

/* The id filters take a comma separated list of ids. Anything else is not a
   number the backend can look up — it answers 400 — so it is caught here and
   the request is never sent. */
const ID_LIST = /^\d+(,\d+)*$/;

const SEARCH_DEBOUNCE_MS = 300;

/* One array, reused: setting the same reference back is a no-op, and clearing
   the list is on the path the effect takes whenever there is nothing to ask
   for. A fresh [] there would be a new state on every run. */
const NO_HITS: SearchHit[] = [];

/* What the reader was looking for, picked out of the name that was found. */
const highlight = (name: string, needle: string) => {
    const at = needle ? name.toLowerCase().indexOf(needle.toLowerCase()) : -1;
    if (at < 0) return name;
    return (
        <>
            {name.slice(0, at)}
            <mark className="nsb__mark">{name.slice(at, at + needle.length)}</mark>
            {name.slice(at + needle.length)}
        </>
    );
};

type Row =
    | { kind: 'filter', filter: typeof FILTERS[number] }
    | { kind: 'hit', hit: SearchHit, icon: IconDefinition, path: string };

const SearchBar = (props: { style?: React.CSSProperties, className?: string }) => {
    const dataProvider: ExtendedDataProvider = useDataProvider();
    const history = useHistory();
    const input = useRef<HTMLInputElement>(null);
    /* Whichever element the cursor is on — a result row or a filter chip — so
       the arrows can bring it into view. A callback, because the two are not
       the same kind of element. */
    const active = useRef<HTMLElement | null>(null);
    const follow = (element: HTMLElement | null) => { active.current = element; };
    const bar = useRef<HTMLDivElement>(null);
    const panelBox = useRef<HTMLDivElement>(null);
    const listBox = useRef<HTMLDivElement>(null);
    /* Which of the two moved the cursor last. Only the arrows may drag the
       viewport after it. */
    const byKeyboard = useRef(false);

    const [filter, setFilter] = useState<FilterKey | null>(null);
    const [term, setTerm] = useState('');
    const [open, setOpen] = useState(false);
    const [hits, setHits] = useState<SearchHit[]>(NO_HITS);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [more, setMore] = useState(false);
    const [cursor, setCursor] = useState(0);
    const [width, setWidth] = useState<number>();

    /* Keystrokes overtake each other: the reply to "vm" must not land on top
       of the reply to "vmw" typed after it. Only the newest request is kept,
       and a page that arrives for an abandoned search is dropped. */
    const latestRequest = useRef(0);
    /* Set before the request rather than with the state below it: two scroll
       events can land in the same frame, and state has not moved yet. */
    const fetchingMore = useRef(false);

    const token = FILTERS.find(item => item.key === filter) ?? null;
    const query = term.trim();
    const ids = query.replace(/\s/g, '');
    const invalidId = !!token && ids !== '' && !ID_LIST.test(ids);

    /* Held rather than depended on. react-admin memoises the provider through
       a chain of four hooks, and if any link in it ever hands back a new
       object the search effect below would re-run on renders that asked no new
       question — restarting the debounce, so a fast typist or a mouse moving
       over the rows could hold the request off indefinitely. */
    const provider = useRef(dataProvider);
    provider.current = dataProvider;

    const ask = (offset: number) => provider.current.getAction('search', {
        // the term is a path segment, so a name with a space or a slash in it
        // has to survive the trip as one segment
        verb: encodeURIComponent(filter ? ids : query),
        filter: {
            limit: PAGE_SIZE,
            ...(offset ? { offset } : {}),
            ...(filter ? { [filter]: ids } : {}),
        },
    });

    useEffect(() => {
        const request = latestRequest.current + 1;
        latestRequest.current = request;
        fetchingMore.current = false;

        if (!query || invalidId) {
            setHits(NO_HITS);
            setTotal(0);
            setLoading(false);
            return undefined;
        }

        setLoading(true);
        const timer = setTimeout(() => {
            ask(0)
                .then((response: any) => {
                    if (latestRequest.current !== request) return;
                    setHits(response?.data?.results ?? NO_HITS);
                    setTotal(response?.data?.count ?? 0);
                    setLoading(false);
                })
                .catch((error: any) => {
                    if (latestRequest.current !== request) return;
                    console.error(error);
                    setHits(NO_HITS);
                    setTotal(0);
                    setLoading(false);
                });
        }, SEARCH_DEBOUNCE_MS);

        return () => clearTimeout(timer);
    }, [query, ids, filter, invalidId]);

    /* The next page, once the reader has scrolled to the end of this one. The
       request belongs to the search that is open now: if a keystroke starts
       another one meanwhile, the page it answers no longer exists and the rows
       are thrown away rather than appended under a different question. */
    const loadMore = () => {
        if (fetchingMore.current || loading || !query || invalidId) return;
        if (hits.length === 0 || hits.length >= total) return;

        const request = latestRequest.current;
        fetchingMore.current = true;
        setMore(true);

        ask(hits.length)
            .then((response: any) => {
                if (latestRequest.current !== request) return;
                const next: SearchHit[] = response?.data?.results ?? [];
                setHits(previous => previous.concat(next));
                setTotal(response?.data?.count ?? 0);
            })
            .catch((error: any) => console.error(error))
            .finally(() => {
                fetchingMore.current = false;
                if (latestRequest.current === request) setMore(false);
            });
    };

    /* Near enough to the end that the next page should already be on its way —
       about a row and a half of runway, so the list does not stall under the
       reader mid-scroll. */
    const onScroll = (event: React.UIEvent<HTMLDivElement>) => {
        const list = event.currentTarget;
        if (list.scrollHeight - list.scrollTop - list.clientHeight < 48) loadMore();
    };

    /* A page that does not fill the box leaves nothing to scroll, and a list
       that cannot be scrolled can never ask for its next page: the reader is
       told there are forty more and the wheel does nothing. Ten rows came to
       379px against a 380px cap, so this was every search. Keep taking pages
       until there is something to scroll, or until there is nothing left. */
    useEffect(() => {
        const list = listBox.current;
        if (!list || loading) return;
        if (list.scrollHeight <= list.clientHeight) loadMore();
    }, [hits, total, loading]);

    /* The panel is one list to the keyboard however many headings it is shown
       under, so the sections carry their rows and the rows are flattened for
       the cursor to walk. */
    const sections = useMemo(() => {
        const out: { label: string, rows: Row[], chips?: boolean }[] = [];

        if (!filter) {
            // still offered while typing, so a name search can be turned into
            // an id one without clearing the bar first
            const offered = FILTERS.filter(item =>
                item.label.toLowerCase().includes(query.toLowerCase()));
            if (offered.length) {
                out.push({
                    label: 'Filter by',
                    chips: true,
                    rows: offered.map(item => ({ kind: 'filter', filter: item })),
                });
            }
        }

        GROUPS.forEach(group => {
            const rows: Row[] = hits
                .filter(hit => hit.model === group.model)
                .map(hit => ({ kind: 'hit', hit, icon: group.icon, path: group.path(hit) }));
            if (rows.length) out.push({ label: group.label, rows });
        });

        return out;
    }, [filter, query, hits]);

    const rows = useMemo(() => sections.flatMap(section => section.rows), [sections]);

    /* A new question starts the cursor at the top. Watching the rows instead
       would drag it back there every time a page was appended underneath. */
    useEffect(() => setCursor(0), [query, filter]);

    // ...but it cannot point past a list that has shrunk
    useEffect(() => {
        setCursor(at => Math.min(at, Math.max(rows.length - 1, 0)));
    }, [rows.length]);

    /* Arrowing to a row off the end brings it into view, which is also what
       asks for the next page — the scroll is what loadMore listens to.

       Only for the arrows, though. The pointer moves the cursor as well, and a
       row that comes under the pointer is on screen already: scrolling to it
       would shove the list back under the reader, so a wheel scroll would
       fight this on every row it passed and never reach the end. */
    useEffect(() => {
        if (!byKeyboard.current) return;
        byKeyboard.current = false;
        active.current?.scrollIntoView?.({ block: 'nearest' });
    }, [cursor]);

    /* The panel is as wide as the bar, which the top bar sizes in vw — so it
       is measured when the panel opens and again when the window changes,
       rather than written down once in the stylesheet. */
    useEffect(() => {
        if (!open) return undefined;
        const measure = () => setWidth(bar.current?.offsetWidth);
        measure();
        window.addEventListener('resize', measure);
        return () => window.removeEventListener('resize', measure);
    }, [open]);

    /* What ends the panel is a press somewhere else, not the input losing the
       focus. Closing on blur would need the panel to hold the focus through a
       mousedown of its own, and preventing that press is also what would stop
       the reader dragging the list's scrollbar — the panel would be scrollable
       by wheel only, and paging would stall for anyone who reached for it. */
    useEffect(() => {
        if (!open) return undefined;
        const onPress = (event: MouseEvent) => {
            const target = event.target as Node;
            if (bar.current?.contains(target) || panelBox.current?.contains(target)) return;
            setOpen(false);
        };
        document.addEventListener('mousedown', onPress);
        return () => document.removeEventListener('mousedown', onPress);
    }, [open]);

    const reset = () => {
        setOpen(false);
        setTerm('');
        setFilter(null);
        setHits(NO_HITS);
        setTotal(0);
    };

    const activate = (row: Row) => {
        if (row.kind === 'filter') {
            setFilter(row.filter.key);
            setTerm('');
            input.current?.focus();
            return;
        }
        reset();
        history.push(row.path);
    };

    const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Escape') {
            setOpen(false);
            return;
        }
        // the token is in front of the text, so it is what backspace reaches
        // once there is no text left to delete
        if (event.key === 'Backspace' && !term && filter) {
            setFilter(null);
            return;
        }
        if (!open) {
            if (event.key === 'ArrowDown') setOpen(true);
            return;
        }
        if (event.key === 'ArrowDown') {
            event.preventDefault();
            byKeyboard.current = true;
            setCursor(at => Math.min(at + 1, rows.length - 1));
        } else if (event.key === 'ArrowUp') {
            event.preventDefault();
            byKeyboard.current = true;
            setCursor(at => Math.max(at - 1, 0));
        } else if (event.key === 'Enter' && rows[cursor]) {
            event.preventDefault();
            activate(rows[cursor]);
        }
    };

    const note = () => {
        /* Rows already found stay where they are while the next answer is on
           its way — blanking them on every keystroke is what makes a search
           feel slow, however quickly the reply comes back. The spinner is for
           the first search only, when there is genuinely nothing to look at. */
        if (loading) return hits.length ? null : <div className="nsb__note"><Spin size="small" /></div>;
        if (invalidId) {
            return (
                <div className="nsb__note">
                    An id is a number. Type one, or several separated by commas.
                </div>
            );
        }
        if (!query) {
            return token
                ? <div className="nsb__note nsb__note--quiet">{`Type an id to ${token.hint}.`}</div>
                : null;
        }
        if (!hits.length) {
            return (
                <div className="nsb__note nsb__note--quiet">
                    Nothing matches <strong>{query}</strong>.
                </div>
            );
        }
        return null;
    };

    const footer = (() => {
        if (invalidId || !query) return null;
        // while the rows stand, the footer is the only thing that admits a
        // newer answer is coming
        if (loading) return hits.length ? 'Searching…' : null;
        if (!hits.length) return null;
        return hits.length >= total
            ? `All ${total} result${total === 1 ? '' : 's'}`
            : `${hits.length} of ${total} — scroll for more`;
    })();

    let index = -1;

    const panel = (
        // a click in the panel must not take the focus off the input first:
        // the blur would close the panel out from under the click
        <div className="nsb__panel" ref={panelBox} style={{ width }}>
            <div className="nsb__list" role="listbox" ref={listBox} onScroll={onScroll}>
                {sections.map(section => (
                    <div className="nsb__section" key={section.label}>
                        <div className="nsb__heading">{section.label}</div>
                        {/* The filters are two words, not two results: side by
                            side as chips they take one line instead of two, and
                            read as the pair of choices they are. They stay in
                            the same list the arrows walk. */}
                        {section.chips ? (
                            <div className="nsb__chips">
                                {section.rows.map(row => {
                                    index += 1;
                                    const at = index;
                                    const isActive = at === cursor;
                                    if (row.kind !== 'filter') return null;
                                    return (
                                        <button
                                            key={row.filter.key}
                                            ref={isActive ? follow : undefined}
                                            type="button"
                                            title={row.filter.hint}
                                            className={`nsb__chip${isActive ? ' nsb__chip--active' : ''}`}
                                            role="option"
                                            aria-selected={isActive}
                                            onMouseEnter={() => setCursor(at)}
                                            onClick={() => activate(row)}
                                        >
                                            <SearchOutlined />
                                            {row.filter.label}
                                        </button>
                                    );
                                })}
                            </div>
                        ) : section.rows.map(row => {
                            index += 1;
                            const at = index;
                            const isActive = at === cursor;
                            if (row.kind !== 'hit') return null;

                            return (
                                <div
                                    key={`${row.hit.model}-${row.hit.pov}-${row.hit.id}`}
                                    ref={isActive ? follow : undefined}
                                    className={`nsb__row${isActive ? ' nsb__row--active' : ''}`}
                                    role="option"
                                    aria-selected={isActive}
                                    onMouseEnter={() => setCursor(at)}
                                    onClick={() => activate(row)}
                                >
                                    <span className="nsb__row-icon">{FaIcon(row.icon)}</span>
                                    <span className="nsb__row-name" title={row.hit.name}>
                                        {filter ? row.hit.name : highlight(row.hit.name, query)}
                                    </span>
                                    <span className="nsb__row-pov">
                                        {POV_LABELS[row.hit.pov] ?? row.hit.pov}
                                    </span>
                                    <span className="nsb__row-id">#{row.hit.id}</span>
                                </div>
                            );
                        })}
                    </div>
                ))}
                {more && <div className="nsb__more"><Spin size="small" /></div>}
                {note()}
            </div>
            {footer && <div className="nsb__foot">{footer}</div>}
        </div>
    );

    return (
        /* The panel opens on focus and on typing, never on a bare click: a
           click trigger toggles, so clicking back into the field to move the
           caret would shut it, and everything typed after that would be
           searched into a popup nobody could see. It closes on a press
           somewhere else, which the effect above watches for. */
        <Dropdown
            open={open}
            trigger={[]}
            placement="bottomLeft"
            popupRender={() => panel}
        >
            <div
                ref={bar}
                className={['nsb__bar', open ? 'nsb__bar--open' : '', props.className]
                    .filter(Boolean).join(' ')}
                style={props.style}
                // anywhere but the input itself: keep the caret where it is
                // rather than letting the press move the focus to the body
                onMouseDown={event => {
                    if (event.target !== input.current) event.preventDefault();
                }}
                onClick={() => input.current?.focus()}
            >
                <SearchOutlined className="nsb__glass" />
                {token && (
                    <span className="nsb__token">
                        {token.label}
                        <button
                            type="button"
                            className="nsb__token-x"
                            aria-label={`Remove the ${token.label} filter`}
                            onClick={event => {
                                event.stopPropagation();
                                setFilter(null);
                                input.current?.focus();
                            }}
                        >
                            <CloseOutlined />
                        </button>
                    </span>
                )}
                <input
                    ref={input}
                    className="nsb__input"
                    role="combobox"
                    aria-expanded={open}
                    aria-autocomplete="list"
                    aria-label="Search"
                    value={term}
                    placeholder={token ? token.placeholder : 'Search or filter…'}
                    onChange={event => {
                        setTerm(event.target.value);
                        // Escape may have closed it; typing means it is wanted
                        setOpen(true);
                    }}
                    onKeyDown={onKeyDown}
                    onFocus={() => setOpen(true)}
                />
                {/* The token has its own way out, which drops the filter and
                    keeps the text. This one empties the bar. */}
                {(term || filter) && (
                    <button
                        type="button"
                        className="nsb__clear"
                        aria-label="Clear the search"
                        onClick={event => {
                            event.stopPropagation();
                            setTerm('');
                            setFilter(null);
                            input.current?.focus();
                            setOpen(true);
                        }}
                    >
                        <CloseCircleFilled />
                    </button>
                )}
            </div>
        </Dropdown>
    );
};

export default SearchBar;
